import { useMemo, useState } from '@wordpress/element';
import { Button } from '@uconnect/assets/components/ui/button';
import Icon from '@uconnect/assets/components/ui/icon';
import { SearchInput } from '@uconnect/assets/components/ui/input';
import { cn } from '@uconnect/assets/lib/utils';
import type { ThreadListItem } from '../chatApi';
import ClearHistoryButton from './ClearHistoryButton';


function parseThreadDate( isoDate: string ): Date | null {
	const date = new Date( isoDate );
	return Number.isNaN( date.getTime() ) ? null : date;
}

function dateBucket( isoDate: string ): string {
	const date = parseThreadDate( isoDate );
	if ( ! date ) {
		return 'Older';
	}

	const startOfDay = ( d: Date ) => new Date( d.getFullYear(), d.getMonth(), d.getDate() ).getTime();
	const diffDays = Math.floor( ( startOfDay( new Date() ) - startOfDay( date ) ) / 86400000 );

	if ( diffDays <= 0 ) return 'Today';
	if ( diffDays === 1 ) return 'Yesterday';
	if ( diffDays <= 7 ) return 'Previous 7 Days';
	if ( diffDays <= 30 ) return 'Previous 30 Days';
	return 'Older';
}

function formatThreadDate( isoDate: string ): string {
	const date = parseThreadDate( isoDate );
	if ( ! date ) {
		return '';
	}
	return new Intl.DateTimeFormat( undefined, { month: 'short', day: 'numeric' } ).format( date );
}

function groupThreadsByDate( threads: ThreadListItem[] ): Array< [ string, ThreadListItem[] ] > {
	const groups = new Map< string, ThreadListItem[] >();
	for ( const thread of threads ) {
		const bucket = dateBucket( thread.updated_at );
		const existing = groups.get( bucket );
		if ( existing ) {
			existing.push( thread );
		} else {
			groups.set( bucket, [ thread ] );
		}
	}
	return Array.from( groups.entries() );
}

interface ThreadHistoryPanelProps {
	variant?: 'search' | 'admin';
	isExpanded?: boolean;
	containerMaxWidthClasses?: string;
	savedThreads: ThreadListItem[];
	isLoading: boolean;
	isClearing: boolean;
	isDisabled?: boolean;
	deletingThreadId: string | null;
	errorMessage: string | null;
	statusMessage: string;
	onClearHistory: () => void;
	onSelectThread: ( threadId: string ) => void;
	onDeleteThread: ( threadId: string ) => void;
}

function DeleteThreadButton( {
	threadTitle,
	isDeleting,
	isDisabled,
	onDelete,
}: {
	threadTitle: string;
	isDeleting: boolean;
	isDisabled: boolean;
	onDelete: () => void;
} ) {
	return (
		<Button
			variant="mutedInterface"
			size="icon-sm"
			disabled={ isDeleting || isDisabled }
			onClick={ onDelete }
			aria-label={ `Delete ${ threadTitle }` }
			title={ isDeleting ? 'Deleting conversation…' : 'Delete conversation' }
		>
			<Icon
				name={ isDeleting ? 'loader' : 'xmark' }
				className={ isDeleting ? 'animate-spin' : undefined }
			/>
		</Button>
	);
}

function ThreadRow( {
	thread,
	isAdmin,
	isDeleting,
	isDisabled,
	onSelectThread,
	onDeleteThread,
}: {
	thread: ThreadListItem;
	isAdmin: boolean;
	isDeleting: boolean;
	isDisabled: boolean;
	// eslint-disable-next-line no-unused-vars -- interface param name, not a binding
	onSelectThread: ( threadId: string ) => void;
	// eslint-disable-next-line no-unused-vars -- interface param name, not a binding
	onDeleteThread: ( threadId: string ) => void;
} ) {
	const formattedDate = formatThreadDate( thread.updated_at );

	return (
		<div
			className={ cn(
				'py-2! first:pt-0! border-b! last:border-b-0! border-border',
				'flex items-center justify-between gap-3'
			) }
		>
			<Button
				variant="link"
				className={ cn(
					'flex-1 justify-start! text-left! font-normal! text-sm/6!',
					'text-foreground! font-default!',
					'font-normal! tracking-normal! normal-case!',
					'h-auto! min-w-0! p-0!',
					isAdmin ? 'w-0! truncate!' : 'w-auto! break-words whitespace-normal! line-clamp-2'
				) }
				disabled={ isDisabled }
				onClick={ () => onSelectThread( thread.thread_id ) }
			>
				{ thread.title }
			</Button>
			{ isAdmin && formattedDate && (
				<span className="shrink-0 text-xs whitespace-nowrap text-muted-foreground">
					{ formattedDate }
				</span>
			) }
			<DeleteThreadButton
				threadTitle={ thread.title }
				isDeleting={ isDeleting }
				isDisabled={ isDisabled }
				onDelete={ () => onDeleteThread( thread.thread_id ) }
			/>
		</div>
	);
}

export default function ThreadHistoryPanel( {
	variant = 'search',
	isExpanded = false,
	containerMaxWidthClasses,
	savedThreads,
	isLoading,
	isClearing,
	isDisabled = false,
	deletingThreadId,
	errorMessage,
	statusMessage,
	onClearHistory,
	onSelectThread,
	onDeleteThread,
}: ThreadHistoryPanelProps ) {
	const isAdmin = variant === 'admin';
	const [ searchQuery, setSearchQuery ] = useState( '' );

	const visibleThreads = useMemo( () => {
		if ( ! isAdmin || ! searchQuery.trim() ) {
			return savedThreads;
		}
		const query = searchQuery.trim().toLowerCase();
		return savedThreads.filter( ( thread ) => thread.title.toLowerCase().includes( query ) );
	}, [ isAdmin, savedThreads, searchQuery ] );

	const groupedThreads = useMemo(
		() => ( isAdmin ? groupThreadsByDate( visibleThreads ) : [] ),
		[ isAdmin, visibleThreads ],
	);

	const labels = {
		loading: isAdmin ? 'Loading saved conversations…' : 'Loading saved searches…',
		empty: isAdmin ? 'No saved admin conversations yet.' : 'No saved searches yet.',
		noMatches: 'No conversations match your search.',
		clearVariant: variant,
	};

	const hasNoResults = ! isLoading && ! errorMessage && visibleThreads.length === 0;

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
			<div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
				{ statusMessage }
			</div>

			{ isAdmin && savedThreads.length > 0 && (
				<div className="shrink-0 px-5 pt-2.5 pb-2">
					<SearchInput
						type="search"
						size="sm"
						className="rounded-none! h-10!"
						placeholder="Search history…"
						aria-label="Search conversation history"
						value={ searchQuery }
						onChange={ ( event ) => setSearchQuery( event.target.value ) }
					/>
				</div>
			) }

			<div
				className={ cn(
					'flex min-h-0 flex-1 flex-col overflow-y-auto',
					isAdmin ? 'px-5 py-2!' : 'p-5!',
					isExpanded && containerMaxWidthClasses
				) }
			>
				{ isLoading && (
					<div className="min-h-full content-center py-8 text-center text-sm text-muted-foreground">
						{ labels.loading }
					</div>
				) }

				{ ! isLoading && errorMessage && (
					<div className="min-h-full content-center py-8 text-center text-sm text-destructive">
						{ errorMessage }
					</div>
				) }

				{ hasNoResults && (
					<div className="min-h-full content-center py-8 text-center text-sm text-muted-foreground">
						{ searchQuery.trim() ? labels.noMatches : labels.empty }
					</div>
				) }

				{ ! isLoading && ! errorMessage && ! isAdmin && visibleThreads.map( ( thread ) => (
					<ThreadRow
						key={ thread.thread_id }
						thread={ thread }
						isAdmin={ false }
						isDeleting={ deletingThreadId === thread.thread_id }
						isDisabled={ isDisabled }
						onSelectThread={ onSelectThread }
						onDeleteThread={ onDeleteThread }
					/>
				) ) }

				{ ! isLoading && ! errorMessage && isAdmin && groupedThreads.map( ( [ bucket, threads ] ) => (
					<div key={ bucket } className="mb-3 last:mb-0">
						<div className="pb-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">
							{ bucket }
						</div>
						{ threads.map( ( thread ) => (
							<ThreadRow
								key={ thread.thread_id }
								thread={ thread }
								isAdmin
								isDeleting={ deletingThreadId === thread.thread_id }
								isDisabled={ isDisabled }
								onSelectThread={ onSelectThread }
								onDeleteThread={ onDeleteThread }
							/>
						) ) }
					</div>
				) ) }
			</div>
			<div className={ isAdmin ? 'px-5' : '!px-5' }>
				<ClearHistoryButton
					isExpanded={ isExpanded }
					isVisible={ ! isLoading && savedThreads.length > 0 }
					isLoading={ isClearing }
					isDisabled={ isDisabled }
					onClick={ onClearHistory }
					containerMaxWidthClasses={ containerMaxWidthClasses }
					variant={ labels.clearVariant }
				/>
			</div>
		</div>
	);
}
